import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import i18n from "../../../i18n";
import { useRillContext } from "../../../rill/RillReact/share/base";
import {
  isUnchanged,
  validateCreateQuestion,
  validateCreateTemplate,
} from "../../../utils/questionnaire-validations";
import scriptLoader from "../../../utils/scriptLoader";
import QuestionCreate from "../../../views/athlete-portal/profile/components/QuestionCreate";
import QuestionDisplay from "../../../views/athlete-portal/profile/components/QuestionDisplay";
import SvgIcon from "../../SvgIcon";

const AddQuestionnaireTemplate = ({
  template,
  userToLoadInformation,
  organizationOwner,
  fromEdit,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();

  const defaultTemplate =
    organizationOwner?.questionnaireTemplates?.defaultTemplate ||
    userToLoadInformation?.questionnaireTemplates?.defaultTemplate ||
    null;

  const [questions, setQuestions] = useState(
    template ? template.questions : []
  );

  const [adding, setAdding] = useState(template ? false : true);
  const [name, setName] = useState(template ? template.title : "");
  const [addNew, setAddNew] = useState(true);
  const [firstEdition, setFirstEdition] = useState(template ? true : false);

  const [asDefault, setAsDefault] = useState(
    template
      ? defaultTemplate
        ? defaultTemplate === template?._id
        : false
      : false
  );
  const [editedQuestions, setEditedQuestions] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState({
    title: "",
    type: "short",
    body: {
      description: i18n.t(
        "athlete-portal:questionTypeDefaults.shortDescription"
      ),
    },
  });
  const [questionOrder, setQuestionOrder] = useState(null);

  const { assert, flash } = locals;

  const handleSetDefault = () => {
    setAsDefault(!asDefault);
  };

  const handleNameChange = (e) => {
    const { value } = e.target;

    setName(value);
  };

  useEffect(() => {
    if (!firstEdition && !adding) {
      setCurrentQuestion({ title: "", type: "", body: {} });
      setAdding(true);
    }
    if (questions.length === 0) {
      setEditedQuestions({});
      setAddNew(true);
    }
  }, [questions]);

  useEffect(() => {
    if (document.querySelector(".principal-grabber-div-template")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div-template"),
            {
              animation: 350,
              handle: ".drag-handle-template",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const newOrderedQuestions = Array.from(
                  document.querySelectorAll(".item-grabber-container-template")
                ).map((question) => question.id);

                setQuestionOrder(newOrderedQuestions);
              },
            }
          );
        }
      );
    }
  }, []);

  const handleAddNew = () => {
    setFirstEdition(false);
    setAdding(true);
  };

  const handleSaveQuestion = () => {
    const isCreateValid = validateCreateQuestion(
      currentQuestion,
      assert,
      flash
    );

    if (isCreateValid) {
      const newQuestion = {
        ...currentQuestion,
        _id: `${currentQuestion.title}-${questions.length}`,
      };

      setQuestions([...questions, newQuestion]);
      setAdding(false);
      setAddNew(true);
    }
  };

  const onEdit = (id) => {
    const question = questions.find((q) => q._id === id);
    if (question) {
      const normalized = {
        ...question,
        body: question.body ?? {
          description: i18n.t(
            "athlete-portal:questionTypeDefaults.shortDescription"
          ),
        },
      };
      setEditedQuestions((prev) => ({ ...prev, [id]: normalized }));
      setFirstEdition(false);
    }
  };

  const onDelete = (id) => {
    const newQuestions = questions.filter((q) => q._id !== id);
    setQuestions(newQuestions);
    setEditedQuestions((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    if (!newQuestions.length) {
      setAdding(true);
      setAddNew(false);
    }
  };

  const mergeEditedIntoQuestions = (questionsList, editedMap) =>
    questionsList.map((q) => editedMap[q._id] ?? q);

  const getQuestionsArray = () => {
    const editedIds = Object.keys(editedQuestions);
    const allEditedValid =
      editedIds.length === 0 ||
      editedIds.every((id) =>
        validateCreateQuestion(editedQuestions[id], assert, flash)
      );
    if (!allEditedValid) return false;

    const baseQuestions = mergeEditedIntoQuestions(questions, editedQuestions);
    const isCurrentQuestionUnchanged = isUnchanged(currentQuestion);
    const isCurrentQuestionValid =
      !isCurrentQuestionUnchanged &&
      validateCreateQuestion(currentQuestion, assert, flash);

    if (isCurrentQuestionUnchanged) {
      return baseQuestions;
    }
    if (isCurrentQuestionValid) {
      const newQuestion = {
        ...currentQuestion,
        _id: `${currentQuestion.title}-${questions.length}`,
      };
      return [...baseQuestions, newQuestion];
    }
    return false;
  };

  const handleSaveTemplate = () => {
    const questionsArray = getQuestionsArray();

    if (!questionsArray) return false;

    let orderedQuestions = [...questionsArray];

    if (questionOrder) {
      const orderMap = new Map(questionOrder.map((id, index) => [id, index]));

      // Just in case someone has a question being created, it should go to the bottom of the array
      orderedQuestions = [...questionsArray].sort((a, b) => {
        const indexA = orderMap.has(a._id) ? orderMap.get(a._id) : Infinity;
        const indexB = orderMap.has(b._id) ? orderMap.get(b._id) : Infinity;
        return indexA - indexB;
      });
    }

    const newQuestions = orderedQuestions.map((question) => ({
      title: question.title,
      type: question.type,
      body: { ...question.body },
    }));

    const newTemplate = {
      title: name,
      questions: [...newQuestions],
    };

    if (validateCreateTemplate(newTemplate, assert, flash)) {
      const baseUrl = template
        ? `/actions/questionnaire/add-template?templateId=${template._id}`
        : `/actions/questionnaire/add-template`;
      const url = fromEdit
        ? `${baseUrl}${template ? "&" : "?"}fromEdit=true`
        : baseUrl;

      submit(url, {
        method: "POST",
        body: {
          title: newTemplate.title,
          questions: newTemplate.questions,
          isDefault: asDefault,
          templateId: template ? template._id : null,
          userToRequest: organizationOwner?._id || userToLoadInformation?._id,
        },
      });
    }
  };

  return (
    <div className="Modal-Box add-checkin-template-modal">
      <a href={`/actions/refresh?modal=&templateId`} className="overlay" />
      <div className="box">
        <header>
          <h2 className="only-show-mobile">
            {template
              ? t("addQuestionnaireTemplate.editTitle")
              : t("addQuestionnaireTemplate.addTitle")}
          </h2>
          <input
            type="text"
            className="input-template-title mobile-hidden"
            placeholder={t("addQuestionnaireTemplate.enterTitlePlaceholder")}
            value={name}
            name="template-name"
            onChange={handleNameChange}
          />
          <a
            href={`/actions/refresh?modal=&templateId=`}
            role="button"
            data-button-type="tertiary"
            data-icon-only="true"
          >
            <img src="/img/new-icons/red-cross.svg" alt={t("common:close")} />
          </a>
        </header>

        <div className="body">
          <label className="force-only-show-mobile">
            <p className="p4">{t("addQuestionnaireTemplate.templateName")}</p>
            <input
              name="template-name"
              className="input-template-title"
              placeholder={t("addQuestionnaireTemplate.enterTitleHere")}
              value={name}
              onChange={handleNameChange}
            />
          </label>
          <div className="questionnaire-add-template-questions">
            <div className="principal-grabber-div-template">
              {!!questions.length &&
                questions.map((question) => {
                  const isEditing = editedQuestions[question._id];

                  if (isEditing) {
                    return (
                      <div
                        className="item-grabber-container-template"
                        id={question._id}
                        key={`${question._id}-template`}
                      >
                        <QuestionCreate
                          data={editedQuestions[question._id]}
                          setData={(newDataOrUpdater) =>
                            setEditedQuestions((prev) => {
                              const current = prev[question._id];
                              const next =
                                typeof newDataOrUpdater === "function"
                                  ? newDataOrUpdater(current)
                                  : newDataOrUpdater;
                              return { ...prev, [question._id]: next };
                            })
                          }
                          isEdited={true}
                          key={`${question._id}`}
                        />
                        <div className="drag-handle-template">
                          <SvgIcon
                            src="/img/new-icons/menu.svg"
                            alt={t("addQuestionnaireTemplate.grabAlt")}
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      className="item-grabber-container-template"
                      id={question._id}
                      key={`${question._id}-template`}
                    >
                      <QuestionDisplay
                        body={question.body}
                        title={question.title}
                        type={question.type}
                        id={question._id}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        key={`${question._id}`}
                      />

                      <div className="drag-handle-template">
                        <SvgIcon
                          src="/img/new-icons/menu.svg"
                          alt={t("addQuestionnaireTemplate.grabAlt")}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>

            {adding && (
              <QuestionCreate
                data={currentQuestion}
                setData={setCurrentQuestion}
              />
            )}

            {addNew && (
              <div className="template-modal-buttons add-acc-new-footer-buttons">
                <button
                  data-button-type="outline"
                  onClick={firstEdition ? handleAddNew : handleSaveQuestion}
                >
                  {t("addQuestionnaireTemplate.addNew")}
                </button>
                <a
                  href={`/actions/refresh?modal=&templateId=`}
                  className=" only-show-mobile acc-btn add-new"
                >
                  {t("common:cancel")}
                </a>
              </div>
            )}
          </div>
        </div>

        <footer>
          {!!defaultTemplate &&
          template &&
          defaultTemplate === template?._id ? (
            <div>
              <p className="p4 opacity-70">
                {t("addQuestionnaireTemplate.defaultTemplateNote")}
              </p>
            </div>
          ) : (
            <div onClick={handleSetDefault}>
              <SvgIcon
                src={`/img/new-icons/${
                  asDefault ? "" : "un"
                }checked-square.svg`}
                noFilterOnLight={asDefault ? true : false}
              />
              <p className="p4">{t("addQuestionnaireTemplate.setAsDefault")}</p>
            </div>
          )}

          <button onClick={handleSaveTemplate} data-button-type="primary">
            {t("common:save")}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AddQuestionnaireTemplate;
